import React, { useState, useEffect } from 'react';
import { X, Filter, Download, RefreshCw, CheckCircle, XCircle, Clock, Zap, Tv, Eye } from 'lucide-react';
import useBillsStore from '../../../store/billsStore';
import billsAPI from '../../../services/bills.api.js';
import { toast } from 'react-hot-toast';
import BillTransactionDetailsModal from './BillTransactionDetailsModal';

const BillTransactionHistory = ({ onClose }) => {
  const [transactions, setTransactions] = useState([]);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [filters, setFilters] = useState({
    billType: 'all',
    status: 'all',
    days: 7,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadTransactions();
  }, [filters]);

  const loadTransactions = async () => {
    setIsLoading(true);
    try {
      const params = {
        ...(filters.billType !== 'all' && { billType: filters.billType }),
        ...(filters.status !== 'all' && { status: filters.status }),
        startDate: new Date(Date.now() - filters.days * 24 * 60 * 60 * 1000).toISOString(),
      };
      
      const response = await billsAPI.getTransactions(params);
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Failed to load transactions:', error);
      toast.error('Failed to load transactions');
    } finally {
      setIsLoading(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const transactionDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    const diffTime = today - transactionDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return `Today, ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `Yesterday, ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'SUCCESS':
        return <CheckCircle className="h-3.5 w-3.5 text-green-600" />;
      case 'PENDING':
        return <Clock className="h-3.5 w-3.5 text-yellow-600" />;
      case 'FAILED':
        return <XCircle className="h-3.5 w-3.5 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'SUCCESS':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'PENDING':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'FAILED':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getBillTypeIcon = (billType) => {
    switch (billType) {
      case 'ELECTRICITY':
        return <Zap className="h-3.5 w-3.5 text-yellow-600" />;
      case 'TV_SUBSCRIPTION':
        return <Tv className="h-3.5 w-3.5 text-purple-600" />;
      default:
        return null;
    }
  };

  const getBillTypeColor = (billType) => {
    switch (billType) {
      case 'ELECTRICITY':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'TV_SUBSCRIPTION':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const handleRetry = async (transactionId) => {
    try {
      await billsAPI.retryTransaction(transactionId);
      toast.success('Transaction retry initiated');
      loadTransactions();
    } catch (error) {
      toast.error('Failed to retry transaction');
    }
  };

  const handleExport = () => {
    if (transactions.length === 0) {
      toast.error('No transactions to export');
      return;
    }

    const csv = transactions.map(t => ({
      Date: formatDate(t.createdAt),
      Type: t.billType,
      Recipient: t.recipient,
      Amount: t.amount,
      Status: t.status,
      Reference: t.vtuReference || t.id,
    }));

    const csvContent = "data:text/csv;charset=utf-8," 
      + Object.keys(csv[0]).join(",") + "\n"
      + csv.map(row => Object.values(row).join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `bills_transactions_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Export successful!');
  };

  const filterOptions = [
    { id: 'billType', label: 'Bill Type', options: [
      { value: 'all', label: 'All Bill Types' },
      { value: 'ELECTRICITY', label: 'Electricity' },
      { value: 'TV_SUBSCRIPTION', label: 'TV Subscription' }
    ]},
    { id: 'status', label: 'Status', options: [
      { value: 'all', label: 'All Status' },
      { value: 'SUCCESS', label: 'Successful' },
      { value: 'PENDING', label: 'Pending' },
      { value: 'FAILED', label: 'Failed' }
    ]},
    { id: 'days', label: 'Date Range', options: [
      { value: 1, label: 'Last 24 hours' },
      { value: 7, label: 'Last 7 days' },
      { value: 30, label: 'Last 30 days' },
      { value: 90, label: 'Last 90 days' }
    ]}
  ];

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-3 sm:px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

          <div className="inline-block align-bottom bg-white rounded-xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-neomorph transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6 border border-primary-100/30 w-full max-w-[95%] sm:max-w-4xl">
            <div className="absolute top-0 right-0 pt-3 pr-3 sm:pt-4 sm:pr-4">
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 bg-[#E8ECF5] rounded-lg p-1.5 shadow-neomorph-inset"
              >
                <X className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            </div>

            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <div className="flex items-center mb-1">
                  <div className="w-1 h-4 sm:h-5 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
                  <h3 className="text-base sm:text-lg font-semibold text-gray-800">
                    Bill Transaction History
                  </h3>
                </div>
              </div>
            </div>

            <div className="mt-5 sm:mt-6 space-y-4 sm:space-y-5">
              {/* Filters - Mobile Dropdown */}
              <div className="bg-[#E8ECF5] rounded-xl p-3 sm:p-4 shadow-neomorph-inset">
                {/* Mobile: Filter button */}
                <div className="sm:hidden">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-white rounded-lg shadow-neomorph"
                  >
                    <div className="flex items-center space-x-2">
                      <Filter className="h-4 w-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">Filter Transactions</span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {filters.billType !== 'all' || filters.status !== 'all' || filters.days !== 7 ? 'Active' : ''}
                    </span>
                  </button>
                  
                  {showFilters && (
                    <div className="mt-3 space-y-3">
                      {filterOptions.map((filterGroup) => (
                        <div key={filterGroup.id}>
                          <label className="block text-xs font-medium text-gray-600 mb-1.5">{filterGroup.label}</label>
                          <select
                            value={filters[filterGroup.id]}
                            onChange={(e) => setFilters({ ...filters, [filterGroup.id]: e.target.value })}
                            className="w-full px-3 py-2 bg-white rounded-lg border-0 shadow-neomorph text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                          >
                            {filterGroup.options.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      ))}
                      <div className="flex space-x-2 pt-2">
                        <button
                          onClick={loadTransactions}
                          disabled={isLoading}
                          className="flex-1 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg px-3 py-2 text-sm font-medium flex items-center justify-center"
                        >
                          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                          Apply Filters
                        </button>
                        <button
                          onClick={handleExport}
                          className="flex-1 bg-white rounded-lg px-3 py-2 shadow-neomorph text-sm font-medium text-gray-700 flex items-center justify-center border border-gray-100"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Export
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Desktop: Filter bar */}
                <div className="hidden sm:flex sm:flex-wrap sm:items-center justify-between gap-3">
                  <div className="flex items-center space-x-2">
                    <Filter className="h-4 w-4 text-gray-400" />
                    <span className="text-xs font-medium text-gray-700">Filter by:</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <select
                      value={filters.billType}
                      onChange={(e) => setFilters({ ...filters, billType: e.target.value })}
                      className="px-3 py-1.5 bg-white rounded-lg border-0 shadow-neomorph text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                    >
                      <option value="all">All Bill Types</option>
                      <option value="ELECTRICITY">Electricity</option>
                      <option value="TV_SUBSCRIPTION">TV Subscription</option>
                    </select>
                    <select
                      value={filters.status}
                      onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                      className="px-3 py-1.5 bg-white rounded-lg border-0 shadow-neomorph text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                    >
                      <option value="all">All Status</option>
                      <option value="SUCCESS">Successful</option>
                      <option value="PENDING">Pending</option>
                      <option value="FAILED">Failed</option>
                    </select>
                    <select
                      value={filters.days}
                      onChange={(e) => setFilters({ ...filters, days: parseInt(e.target.value) })}
                      className="px-3 py-1.5 bg-white rounded-lg border-0 shadow-neomorph text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                    >
                      <option value={1}>Last 24 hours</option>
                      <option value={7}>Last 7 days</option>
                      <option value={30}>Last 30 days</option>
                      <option value={90}>Last 90 days</option>
                    </select>
                    <button
                      onClick={loadTransactions}
                      disabled={isLoading}
                      className="bg-white rounded-lg px-3 py-1.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-xs font-medium text-gray-700 flex items-center border border-gray-100"
                    >
                      <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </button>
                    <button
                      onClick={handleExport}
                      className="bg-white rounded-lg px-3 py-1.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-xs font-medium text-gray-700 flex items-center border border-gray-100"
                    >
                      <Download className="h-3.5 w-3.5 mr-1.5" />
                      Export
                    </button>
                  </div>
                </div>
              </div>

              {/* Transactions List - Mobile Card View, Desktop Table */}
              <div className="bg-white rounded-xl overflow-hidden border border-gray-100">
                {/* Mobile: Card View */}
                <div className="sm:hidden divide-y divide-gray-200">
                  {isLoading ? (
                    <div className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <RefreshCw className="h-8 w-8 animate-spin text-primary-600 mb-3" />
                        <p className="text-sm text-gray-500">Loading transactions...</p>
                      </div>
                    </div>
                  ) : transactions.length > 0 ? (
                    transactions.map((transaction) => (
                      <div key={transaction.id} className="p-4 hover:bg-[#E8ECF5] transition-colors">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center space-x-2">
                            <div className={`p-1.5 rounded-lg ${transaction.billType === 'ELECTRICITY' ? 'bg-yellow-50' : 'bg-purple-50'} shadow-neomorph-inset`}>
                              {getBillTypeIcon(transaction.billType)}
                            </div>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium border ${getBillTypeColor(transaction.billType)}`}>
                              {transaction.billType.replace('_', ' ')}
                            </span>
                          </div>
                          <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium border ${getStatusColor(transaction.status)}`}>
                            {getStatusIcon(transaction.status)}
                            <span className="ml-1 hidden sm:inline">{transaction.status}</span>
                          </span>
                        </div>
                        
                        <div className="mb-2">
                          <p className="text-sm font-medium text-gray-800 break-words">{transaction.recipient}</p>
                          <p className="text-xs text-gray-500">{transaction.productDetails?.provider || 'N/A'}</p>
                        </div>
                        
                        <div className="flex justify-between items-center mb-3">
                          <div className="text-xs text-gray-500">
                            {formatDate(transaction.createdAt)}
                          </div>
                          <div className="text-base font-bold text-gray-800">
                            {formatAmount(transaction.amount)}
                          </div>
                        </div>
                        
                        <div className="flex space-x-2 pt-2 border-t border-gray-100">
                          <button
                            onClick={() => setSelectedTransaction(transaction)}
                            className="flex-1 text-xs text-primary-600 hover:text-primary-700 bg-primary-50 rounded-lg px-3 py-2 shadow-neomorph-inset flex items-center justify-center"
                          >
                            <Eye className="h-3.5 w-3.5 mr-1.5" />
                            Details
                          </button>
                          {transaction.status === 'FAILED' && (
                            <button
                              onClick={() => handleRetry(transaction.id)}
                              className="flex-1 text-xs text-primary-600 hover:text-primary-700 bg-primary-50 rounded-lg px-3 py-2 shadow-neomorph-inset"
                            >
                              Retry
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 bg-gradient-to-br from-primary-100 to-primary-200 rounded-xl flex items-center justify-center mb-3">
                          <Filter className="h-6 w-6 text-primary-600" />
                        </div>
                        <p className="text-sm text-gray-500 mb-1">No transactions found</p>
                        <p className="text-xs text-gray-400">Try changing your filters</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Desktop: Table View */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-[#E8ECF5]">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Details
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {isLoading ? (
                        <tr>
                          <td colSpan="6" className="px-4 py-12 text-center">
                            <div className="flex flex-col items-center">
                              <RefreshCw className="h-8 w-8 animate-spin text-primary-600 mb-3" />
                              <p className="text-sm text-gray-500">Loading transactions...</p>
                            </div>
                          </td>
                        </tr>
                      ) : transactions.length > 0 ? (
                        transactions.map((transaction) => (
                          <tr key={transaction.id} className="hover:bg-[#E8ECF5] transition-colors">
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-xs text-gray-700">
                                {formatDate(transaction.createdAt)}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className={`p-1 rounded-lg ${transaction.billType === 'ELECTRICITY' ? 'bg-yellow-50' : 'bg-purple-50'} shadow-neomorph-inset mr-2`}>
                                  {getBillTypeIcon(transaction.billType)}
                                </div>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium border ${getBillTypeColor(transaction.billType)}`}>
                                  {transaction.billType.replace('_', ' ')}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm font-medium text-gray-800">
                                {transaction.recipient}
                              </div>
                              <div className="text-xs text-gray-500">
                                {transaction.productDetails?.provider || 'N/A'}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm font-semibold text-gray-800">
                                {formatAmount(transaction.amount)}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium border ${getStatusColor(transaction.status)}`}>
                                {getStatusIcon(transaction.status)}
                                <span className="ml-1">{transaction.status}</span>
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => setSelectedTransaction(transaction)}
                                  className="text-xs text-primary-600 hover:text-primary-700 bg-primary-50 rounded-lg px-2 py-1 shadow-neomorph-inset flex items-center"
                                >
                                  <Eye className="h-3 w-3 mr-1" />
                                  Details
                                </button>
                                {transaction.status === 'FAILED' && (
                                  <button
                                    onClick={() => handleRetry(transaction.id)}
                                    className="text-xs text-primary-600 hover:text-primary-700 bg-primary-50 rounded-lg px-2 py-1 shadow-neomorph-inset"
                                  >
                                    Retry
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="px-4 py-12 text-center">
                            <div className="flex flex-col items-center">
                              <div className="w-12 h-12 bg-gradient-to-br from-primary-100 to-primary-200 rounded-xl flex items-center justify-center mb-3">
                                <Filter className="h-6 w-6 text-primary-600" />
                              </div>
                              <p className="text-sm text-gray-500 mb-1">No transactions found</p>
                              <p className="text-xs text-gray-400">Try changing your filters</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Transaction Details Modal */}
      {selectedTransaction && (
        <BillTransactionDetailsModal
          transaction={selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
        />
      )}

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </>
  );
};

export default BillTransactionHistory;